import { useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';

import { useAutoAdvance } from '../chipSelect/useAutoAdvance';

export interface ScaleBlockProps<TConfig> {
  data: {
    field_name?: string;
    required?: boolean;
    value?: string;
    blockConfig?: Partial<TConfig>;
  };
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  mutable?: boolean;
  defaultValue?: string;
}

/** Shared state for the scale blocks: current answer, required error, hidden input and auto-advance. */
export const useScaleValue = <TConfig>(
  props: ScaleBlockProps<TConfig>,
  ref: Ref<HTMLInputElement>,
  autoAdvance = false,
) => {
  const { field_name, required } = props.data || {};
  const isLiveForm = props.mutable !== false;
  const interactive = isLiveForm && !props.read_only;

  const [value, setValue] = useState<string>(
    String(props.defaultValue ?? props.data?.value ?? ''),
  );
  const [touched, setTouched] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

  const scheduleAdvance = useAutoAdvance(isLiveForm && autoAdvance);

  const commit = (next: string, advance = false) => {
    setValue(next);
    setTouched(true);
    if (props.onChange && field_name) props.onChange({ [field_name]: next });
    if (advance && next !== '') scheduleAdvance();
  };

  const showError = isLiveForm && touched && Boolean(required) && value.trim() === '';

  return {
    value,
    commit,
    interactive,
    isLiveForm,
    showError,
    hiddenInput: { ref: hiddenInputRef, type: 'hidden' as const, name: field_name, value },
  };
};
